import React from 'react';
import PropTypes from 'prop-types';
import {
  gql,
  useQuery,
  NetworkStatus,
  usePaginationHelper,
} from '@common/hooks';
import moment from 'moment';
import { Typography } from 'antd';

import { FilteredTable } from 'works/components/filtered-table';
import { ErrorHandler } from '@common/components';

const { Text } = Typography;

const PaginatedWorksShiftMatchesQuery = gql`
  query paginatedWorksShiftMatches(
    $filters: WorksShiftMatchesForWorkersFilterInput
    $workerId: ID!
    $limit: Int!
    $offset: Int!
    $orderDirection: OrderDirection
    $orderField: WorksShiftMatchOrderField
  ) {
    paginatedWorksShiftMatches(
      filters: $filters
      workerId: $workerId
      limit: $limit
      offset: $offset
      orderDirection: $orderDirection
      orderField: $orderField
    ) {
      worksShiftMatches {
        id
        formattedRate
        organizationUnit {
          id
          name
          costCenter
        }
        shiftGap {
          id
          shiftDuration
          startDatetime
          timeZone
          role {
            id
            abbreviation
          }
        }
      }
      totalCount
    }
  }
`;

export function WorksShiftMatchesTable({ workerId }) {
  const { paginationVariables, tableChangeHandler } = usePaginationHelper({
    localStorageFilterKey: 'WorksShiftMatchesTableKey',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'start',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      startDatetime: 'start',
      organizationUnit: 'unit',
      shiftDuration: 'duration',
      formattedRate: 'rate',
    },
  });

  const { error, data, networkStatus } = useQuery(
    PaginatedWorksShiftMatchesQuery,
    {
      variables: { ...paginationVariables, workerId },
      notifyOnNetworkStatusChange: true,
    }
  );

  const isLoaded = Boolean(networkStatus === NetworkStatus.ready && data);

  if (error) {
    return <ErrorHandler error={error} log="WorksShiftMatchesTable" />;
  }

  const records = isLoaded
    ? data.paginatedWorksShiftMatches.worksShiftMatches
    : [];

  const total = isLoaded ? data.paginatedWorksShiftMatches.totalCount : null;

  const columns = [
    {
      title: 'Start Time',
      dataIndex: 'startDatetime',
      key: 'startDatetime',
      width: '15%',
      sorter: true,
      render: (_, record) => {
        const startTime = moment
          .parseZone(record.shiftGap.startDatetime)
          .tz(record.shiftGap.timeZone);
        const date = startTime.format('MM/DD/yyyy');
        const time = startTime.format('HH:mm');

        return (
          <>
            <Text>{date}</Text>
            <br />
            <Text>{time}</Text>
          </>
        );
      },
    },
    {
      title: 'Unit / Cost Center',
      dataIndex: 'organizationUnit',
      key: 'organizationUnit',
      width: '30%',
      sorter: true,
      render: (_, record) => {
        const organizationUnitName = record.organizationUnit.name;
        const costCenter = record.organizationUnit.costCenter;
        return (
          <>
            <Text>{organizationUnitName}</Text>
            <br />
            <Text type="secondary">{costCenter}</Text>
          </>
        );
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: '10%',
      sorter: true,
      render: (_, record) => <>{record.shiftGap.role.abbreviation}</>,
    },
    {
      title: 'Duration',
      dataIndex: 'shiftDuration',
      key: 'shiftDuration',
      width: '10%',
      render: (_, record) => {
        const duration = Number(record.shiftGap.shiftDuration);
        return <span>{duration} hrs</span>;
      },
    },
    {
      title: 'Incentive',
      dataIndex: 'formattedRate',
      key: 'formattedRate',
      width: '35%',
      sorter: true,
    },
  ];

  return (
    <FilteredTable
      rowKey="id"
      columns={columns}
      dataSource={records}
      loading={!isLoaded}
      pagination={{
        ...paginationVariables,
        total,
      }}
      onChange={tableChangeHandler}
    />
  );
}

WorksShiftMatchesTable.propTypes = {
  workerId: PropTypes.string.isRequired,
};
